/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

applyPlugin(jsPDF);

function ApelDinas() {
  const [loading, setLoading] = useState(false);
  const [dataApelDinas, setDataApelDinas] = useState(null);
  const [selectedTanggal, setSelectedTanggal] = useState("");
  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const [userStatus, setUserStatus] = useState(null);

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State untuk modal apel
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPegawai, setSelectedPegawai] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editApelId, setEditApelId] = useState(null);
  const [form, setForm] = useState({
    uid_RSS: "",
    tanggal: "",
    jenis_apel: "Pagi",
    jam_absen: "",
    status: "Hadir",
    keterangan: "",
  });

  // State untuk modal detail apel
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedApelDetail, setSelectedApelDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // State untuk switch statistik apel
  const [selectedApelType, setSelectedApelType] = useState("pagi");

  // State untuk catat massal apel
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkType, setBulkType] = useState("Pagi");
  const [selectedPegawaiBulk, setSelectedPegawaiBulk] = useState([]);
  const [loadingBulkSubmit, setLoadingBulkSubmit] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const getCurrentTimeWITA = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };
  const [bulkJam, setBulkJam] = useState(getCurrentTimeWITA());
  const [bulkStatus, setBulkStatus] = useState("Hadir");
  const [bulkKeterangan, setBulkKeterangan] = useState("");

  const statusOptions = [
    "Hadir",
    "Izin",
    "Sakit",
    "Dinas Luar",
    "Tanpa Berita",
    "Cuti",
    "Tugas Belajar",
  ];
  const jenisApelOptions = ["Pagi", "Siang", "Lainnya"];

  // Options pegawai untuk autocomplete massal
  // Filter: hanya tampilkan pegawai yang belum memiliki data apel untuk jenis yang dipilih
  const pegawaiOptions = useMemo(() => {
    if (!dataApelDinas?.data_pegawai) return [];

    return dataApelDinas.data_pegawai
      .filter((p) => {
        // Jika jenis apel adalah "Pagi", filter pegawai yang belum punya apel_pagi
        if (bulkType === "Pagi") {
          return !p.apel_pagi || !p.apel_pagi.id_apel;
        }
        // Jika jenis apel adalah "Siang", filter pegawai yang belum punya apel_siang
        if (bulkType === "Siang") {
          return !p.apel_siang || !p.apel_siang.id_apel;
        }
        // Untuk jenis lainnya, tampilkan semua
        return true;
      })
      .map((p) => ({
        label: p.nama_pegawai || "",
        value: p.uid_RSS,
        uid_RSS: p.uid_RSS,
        avatar: `https://smartdh.rotendaokab.go.id/uploads/${p.uid_RSS}.jpg`,
      }));
  }, [dataApelDinas, bulkType]);

  // Fungsi untuk mendapatkan jam saat ini

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid);
    setUserDinasId(user.dinas_id);
    setUserStatus(user.status || user.role);

    // Set default tanggal ke hari ini dengan WITA (UTC+8)
    const today = new Date();
    const witaOffset = 8 * 60; // UTC+8 dalam menit
    const witaDate = new Date(today.getTime() + witaOffset * 60 * 1000);
    const todayWITA = witaDate.toISOString().split("T")[0];
    setSelectedTanggal(todayWITA);

    // Auto-select dinas berdasarkan user yang login
    if (user.dinas_id) {
      setUserDinasId(user.dinas_id);
    }

    // Inisialisasi form dengan jam WITA saat ini
    const currentTimeWITA = getCurrentTimeWITA();
    setForm((prevForm) => ({
      ...prevForm,
      jam_absen: currentTimeWITA,
    }));
  }, []);

  const fetchApelDinas = () => {
    if (!userDinasId || !selectedTanggal) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    axios
      .get(
        `${API_ESA_OFFICE}apel-harian/dinas/${userDinasId}/tanggal/${selectedTanggal}`
      )
      .then((res) => {
        setDataApelDinas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching apel dinas:", err);
        setLoading(false);
        swal("Error", "Gagal mengambil data apel dinas", "error");
      });
  };

  const handleTanggalChange = (e) => {
    setSelectedTanggal(e.target.value);
  };

  // Auto-fetch data ketika tanggal berubah
  useEffect(() => {
    if (userDinasId && selectedTanggal && !loading) {
      fetchApelDinas();
    }
  }, [selectedTanggal, userDinasId]);

  // Fungsi untuk modal apel
  const handleOpenDialog = (pegawai) => {
    setSelectedPegawai(pegawai);
    setIsEditMode(false);
    setEditApelId(null);

    // Cek apakah sudah ada data apel pagi atau siang
    const apelPagi = pegawai.apel_pagi;
    const apelSiang = pegawai.apel_siang;

    if (apelPagi && apelSiang) {
      // Jika keduanya sudah ada, tanyakan user mau edit yang mana
      swal({
        title: "Pilih Jenis Apel",
        text: "Data apel pagi dan siang sudah ada. Pilih yang ingin diedit:",
        icon: "info",
        buttons: {
          pagi: "Apel Pagi",
          siang: "Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "pagi") {
          openEditDialog(pegawai, apelPagi, "Pagi");
        } else if (value === "siang") {
          openEditDialog(pegawai, apelSiang, "Siang");
        }
      });
    } else if (apelPagi) {
      // Jika hanya apel pagi yang ada, tanyakan mau edit pagi atau tambah siang
      swal({
        title: "Pilih Aksi",
        text: "Data apel pagi sudah ada. Pilih aksi:",
        icon: "info",
        buttons: {
          editPagi: "Edit Apel Pagi",
          addSiang: "Tambah Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "editPagi") {
          openEditDialog(pegawai, apelPagi, "Pagi");
        } else if (value === "addSiang") {
          openAddDialog(pegawai, "Siang");
        }
      });
    } else if (apelSiang) {
      // Jika hanya apel siang yang ada, tanyakan mau edit siang atau tambah pagi
      swal({
        title: "Pilih Aksi",
        text: "Data apel siang sudah ada. Pilih aksi:",
        icon: "info",
        buttons: {
          editSiang: "Edit Apel Siang",
          addPagi: "Tambah Apel Pagi",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "editSiang") {
          openEditDialog(pegawai, apelSiang, "Siang");
        } else if (value === "addPagi") {
          openAddDialog(pegawai, "Pagi");
        }
      });
    } else {
      // Jika belum ada data sama sekali, tanyakan mau tambah yang mana
      swal({
        title: "Pilih Jenis Apel",
        text: "Pilih jenis apel yang ingin ditambahkan:",
        icon: "info",
        buttons: {
          pagi: "Apel Pagi",
          siang: "Apel Siang",
          cancel: "Batal",
        },
      }).then((value) => {
        if (value === "pagi" || value === "siang") {
          openAddDialog(pegawai, value === "pagi" ? "Pagi" : "Siang");
        }
      });
    }
  };

  const openAddDialog = (pegawai, jenisApel) => {
    const currentTimeWITA = getCurrentTimeWITA();

    setSelectedPegawai(pegawai);
    setIsEditMode(false);
    setEditApelId(null);
    setForm({
      uid_RSS: pegawai.uid_RSS.toString(),
      tanggal: selectedTanggal,
      jenis_apel: jenisApel,
      jam_absen: currentTimeWITA, // Default jam saat ini dengan WITA
      status: "Hadir",
      keterangan: "",
    });
    setOpenDialog(true);
  };

  const openEditDialog = (pegawai, apelData, jenisApel) => {
    const currentTimeWITA = getCurrentTimeWITA();
    setSelectedPegawai(pegawai);
    setIsEditMode(true);
    setEditApelId(apelData.id_apel);
    setForm({
      uid_RSS: pegawai.uid_RSS.toString(),
      tanggal: selectedTanggal,
      jenis_apel: jenisApel,
      jam_absen: apelData.jam_absen || currentTimeWITA,
      status: apelData.status || "Hadir",
      keterangan: apelData.keterangan || "",
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    const currentTimeWITA = getCurrentTimeWITA();
    setOpenDialog(false);
    setSelectedPegawai(null);
    setIsEditMode(false);
    setEditApelId(null);
    setForm({
      uid_RSS: "",
      tanggal: "",
      jenis_apel: "Pagi",
      jam_absen: currentTimeWITA, // Default jam saat ini dengan WITA
      status: "Hadir",
      keterangan: "",
    });
  };

  // Bulk dialog handlers
  const handleOpenBulkDialog = (type) => {
    const currentTimeWITA = getCurrentTimeWITA();
    setBulkType(type === "Siang" ? "Siang" : "Pagi");
    setBulkJam(currentTimeWITA);
    setBulkStatus("Hadir");
    setBulkKeterangan("");
    setSelectedPegawaiBulk([]);
    setOpenBulkDialog(true);
  };

  const handleCloseBulkDialog = () => {
    const currentTimeWITA = getCurrentTimeWITA();
    setOpenBulkDialog(false);
    setSelectedPegawaiBulk([]);
    setBulkJam(currentTimeWITA);
    setBulkStatus("Hadir");
    setBulkKeterangan("");
  };

  const handleSelectAllPegawai = () => {
    setSelectedPegawaiBulk(pegawaiOptions);
  };

  const handleBulkSubmit = async () => {
    if (!selectedPegawaiBulk.length) {
      swal("Info", "Pilih minimal satu pegawai", "info");
      return;
    }
    if (!selectedTanggal || !bulkJam) {
      swal("Error", "Tanggal dan jam harus diisi", "error");
      return;
    }

    const payload = selectedPegawaiBulk.map((p) => ({
      uid_RSS: p.uid_RSS?.toString(),
      tanggal: selectedTanggal,
      jenis_apel: bulkType,
      jam_absen: bulkJam,
      status: bulkStatus,
      keterangan: bulkKeterangan || null,
    }));

    setLoadingBulkSubmit(true);
    try {
      await axios.post(`${API_ESA_OFFICE}apel-harian/bulk`, {
        data: payload,
      });
      swal("Success", "Data apel massal berhasil dikirim", "success");
      handleCloseBulkDialog();
      fetchApelDinas();
    } catch (error) {
      console.error("Error bulk submit apel:", error);
      swal(
        "Error",
        error.response?.data?.message ||
          "Gagal mengirim data apel massal. Pastikan endpoint bulk tersedia.",
        "error"
      );
    } finally {
      setLoadingBulkSubmit(false);
    }
  };

  const handleSubmit = () => {
    // Validasi form
    if (!form.tanggal || !form.jenis_apel || !form.status) {
      swal("Error", "Tanggal, jenis apel, dan status harus diisi", "error");
      return;
    }

    const submitData = {
      uid_RSS: form.uid_RSS.toString(),
      tanggal: form.tanggal,
      jenis_apel: form.jenis_apel,
      jam_absen: form.jam_absen || null,
      status: form.status,
      keterangan: form.keterangan || null,
    };

    setLoadingSubmit(true);

    if (isEditMode && editApelId) {
      // Update existing apel
      axios
        .put(`${API_ESA_OFFICE}apel-harian/${editApelId}`, submitData)
        .then((res) => {
          swal("Success", "Data apel harian berhasil diperbarui", "success");
          handleCloseDialog();
          fetchApelDinas(); // Refresh data
        })
        .catch((err) => {
          console.error("Error updating apel harian:", err);
          swal(
            "Error",
            err.response?.data?.message || "Gagal memperbarui data apel harian",
            "error"
          );
        })
        .finally(() => {
          setLoadingSubmit(false);
        });
    } else {
      // Create new apel
      axios
        .post(`${API_ESA_OFFICE}apel-harian`, submitData)
        .then((res) => {
          swal("Success", "Data apel harian berhasil dibuat", "success");
          handleCloseDialog();
          fetchApelDinas(); // Refresh data
        })
        .catch((err) => {
          console.error("Error creating apel harian:", err);
          swal(
            "Error",
            err.response?.data?.message || "Gagal membuat data apel harian",
            "error"
          );
        })
        .finally(() => {
          setLoadingSubmit(false);
        });
    }
  };

  // Fungsi untuk modal detail apel
  const handleOpenDetailModal = (pegawai) => {
    setSelectedApelDetail(pegawai);
    setOpenDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedApelDetail(null);
  };

  // Statistik untuk summary dan export (gabungan pagi + siang)
  const statistikApel = useMemo(() => {
    if (!dataApelDinas?.data_pegawai?.length) {
      return {
        totalPegawai: 0,
        hadir: 0,
        izin: 0,
        sakit: 0,
        lainnya: 0,
      };
    }
    const pegawai = dataApelDinas.data_pegawai;
    const hasStatus = (p, status) =>
      p.apel_pagi?.status === status || p.apel_siang?.status === status;
    const statusLainnya = [
      "Dinas Luar",
      "Tanpa Berita",
      "Cuti",
      "Tugas Belajar",
    ];
    return {
      totalPegawai: pegawai.length,
      hadir: pegawai.filter((p) => hasStatus(p, "Hadir")).length,
      izin: pegawai.filter((p) => hasStatus(p, "Izin")).length,
      sakit: pegawai.filter((p) => hasStatus(p, "Sakit")).length,
      lainnya: pegawai.filter((p) =>
        statusLainnya.some((s) => hasStatus(p, s))
      ).length,
    };
  }, [dataApelDinas]);

  const getExportFilename = (ext) => {
    const namaDinas =
      dataApelDinas?.nama_dinas?.replace(/[^a-zA-Z0-9\s-]/g, "") || "Dinas";
    const tanggalFormatted = selectedTanggal
      ? new Date(selectedTanggal).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).replace(/\//g, "-")
      : "";
    const hadirCount = statistikApel.hadir;
    return `Esa Office - ${namaDinas} - ${tanggalFormatted} - Hadir - ${hadirCount} orang.${ext}`;
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!dataApelDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Apel Pagi",
      "Status Pagi",
      "Apel Siang",
      "Status Siang",
    ];

    const pegawaiForExport = [...(dataApelDinas.data_pegawai || [])].sort(
      (a, b) => {
        const levelA = a.level_jabatan ?? 999;
        const levelB = b.level_jabatan ?? 999;
        if (levelA !== levelB) return levelA - levelB;
        return (a.nama_pegawai || "").localeCompare(b.nama_pegawai || "");
      }
    );
    let tableRows = "";
    pegawaiForExport.forEach((pegawai, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${pegawai.nama_pegawai || ""}</td>
          <td>${pegawai.apel_pagi?.jam_absen || "Belum Absen"}</td>
          <td>${pegawai.apel_pagi?.status || "-"}</td>
          <td>${pegawai.apel_siang?.jam_absen || "Belum Absen"}</td>
          <td>${pegawai.apel_siang?.status || "-"}</td>
        </tr>
      `;
    });

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userName = user?.nama || user?.name || "User";

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Apel Harian Dinas - ${selectedTanggal}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .header-small { font-size: 11px; font-weight: bold; margin-bottom: 4px; }
            .footer-info { font-size: 9px; color: #666; margin-top: 8px; }
          </style>
        </head>
        <body>
          <p class="header-small">Apel Harian Dinas - ${dataApelDinas.nama_dinas} - ${new Date(
      selectedTanggal
    ).toLocaleDateString("id-ID")}</p>
          <h3>Ringkasan:</h3>
          <ul>
            <li>Jumlah Pegawai: ${statistikApel.totalPegawai}</li>
            <li>Jumlah Hadir: ${statistikApel.hadir}</li>
            <li>Jumlah Izin: ${statistikApel.izin}</li>
            <li>Jumlah Sakit: ${statistikApel.sakit}</li>
            <li>Lainnya (Dinas Luar, Tanpa Berita, Cuti, Tugas Belajar): ${statistikApel.lainnya}</li>
            <li>Tanggal: ${new Date(dataApelDinas.tanggal).toLocaleDateString(
              "id-ID"
            )}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
          <p class="footer-info">Download dari Esa Office oleh ${userName}</p>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, getExportFilename("xls"));
  };

  const sanitizeForPDF = (val) => {
    if (val == null) return "";
    const s = String(val).replace(/\r\n/g, " ").replace(/\n/g, " ");
    return s.length > 200 ? s.substring(0, 200) + "..." : s;
  };

  const getDownloadInfo = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const userNama = user?.nama || user?.name || "User";
    const tanggalDownload = new Date().toLocaleString("id-ID", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return { userNama, tanggalDownload };
  };

  // Fungsi untuk export PDF (menggunakan sistem yang sama dengan perorangDetail.js)
  const handleExportPDF = () => {
    if (!dataApelDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      let yPos = 12;

      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Laporan Apel Harian - ${dataApelDinas.nama_dinas}`, 14, yPos);
      yPos += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Tanggal: ${new Date(selectedTanggal).toLocaleDateString("id-ID", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}`,
        14,
        yPos
      );
      yPos += 8;

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text("Ringkasan:", 14, yPos);
      yPos += 5;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text(
        `Jumlah Pegawai: ${statistikApel.totalPegawai}  |  Hadir: ${statistikApel.hadir}  |  Izin: ${statistikApel.izin}  |  Sakit: ${statistikApel.sakit}  |  Lainnya: ${statistikApel.lainnya}`,
        14,
        yPos
      );
      yPos += 8;

      const headers = [
        ["No", "Nama Pegawai", "Apel Pagi", "Status Pagi", "Apel Siang", "Status Siang"],
      ];
      const pegawaiForPdf = [...(dataApelDinas.data_pegawai || [])].sort(
        (a, b) => {
          const levelA = a.level_jabatan ?? 999;
          const levelB = b.level_jabatan ?? 999;
          if (levelA !== levelB) return levelA - levelB;
          return (a.nama_pegawai || "").localeCompare(b.nama_pegawai || "");
        }
      );
      const body = pegawaiForPdf.map((pegawai, index) => [
        String(index + 1),
        sanitizeForPDF(pegawai.nama_pegawai || ""),
        sanitizeForPDF(pegawai.apel_pagi?.jam_absen || "Belum Absen"),
        sanitizeForPDF(pegawai.apel_pagi?.status || "-"),
        sanitizeForPDF(pegawai.apel_siang?.jam_absen || "Belum Absen"),
        sanitizeForPDF(pegawai.apel_siang?.status || "-"),
      ]);

      doc.autoTable({
        head: headers,
        body,
        startY: yPos,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 7 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
        margin: { left: 14, right: 14 },
        showHead: "everyPage",
        columnStyles: {
          0: { cellWidth: 15 },
          1: { cellWidth: 70 },
          2: { cellWidth: 40 },
          3: { cellWidth: 32 },
          4: { cellWidth: 40 },
          5: { cellWidth: 32 },
        },
      });

      const { userNama, tanggalDownload } = getDownloadInfo();
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        "Laporan ini didownload dari Esa Office App oleh " + userNama + " tanggal " + tanggalDownload,
        14,
        doc.internal.pageSize.getHeight() - 10
      );

      doc.save(getExportFilename("pdf"));
    } catch (err) {
      console.error("PDF export error:", err);
      swal("Error", "Gagal export PDF: " + (err.message || "Unknown error"), "error");
    }
  };

  // Kolom untuk tabel data pegawai
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    // Kolom aksi hanya untuk admin
    ...(userStatus === "admin" || (userStatus === "super admin" && userId === 9)
      ? [
          {
            Header: "Aksi",
            accessor: "actions",
            width: "10%",
            Cell: ({ row }) => (
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={() => handleOpenDialog(row.original)}
              >
                Catat
              </MDButton>
            ),
          },
        ]
      : []),
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "32%" },
    {
      Header: "Apel Pagi",
      accessor: "apel_pagi",
      width: "18%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenDetailModal(row.original)}
          >
            {value?.jam_absen || "Belum Absen"}
          </MDTypography>
          {value?.status && (
            <Chip
              label={value.status}
              color={value.status === "Hadir" ? "success" : "warning"}
              size="small"
              variant="filled"
              sx={{ mt: 0.5 }}
            />
          )}
        </MDBox>
      ),
    },
    {
      Header: "Apel Siang",
      accessor: "apel_siang",
      width: "18%",
      Cell: ({ value, row }) => (
        <MDBox>
          <MDTypography
            variant="caption"
            color="text"
            display="block"
            sx={{ cursor: "pointer" }}
            onClick={() => handleOpenDetailModal(row.original)}
          >
            {value?.jam_absen || "Belum Absen"}
          </MDTypography>
          {value?.status && (
            <Chip
              label={value.status}
              color={value.status === "Hadir" ? "success" : "warning"}
              size="small"
              variant="filled"
              sx={{ mt: 0.5 }}
            />
          )}
        </MDBox>
      ),
    },
  ];

  // Rows untuk tabel data pegawai - urutkan berdasarkan level_jabatan
  const sortedPegawai = useMemo(() => {
    if (!dataApelDinas?.data_pegawai) return [];
    return [...dataApelDinas.data_pegawai].sort((a, b) => {
      const levelA = a.level_jabatan ?? 999;
      const levelB = b.level_jabatan ?? 999;
      if (levelA !== levelB) return levelA - levelB;
      return (a.nama_pegawai || "").localeCompare(b.nama_pegawai || "");
    });
  }, [dataApelDinas?.data_pegawai]);

  const rows =
    sortedPegawai?.map((pegawai, index) => ({
      index: index + 1,
      uid_RSS: pegawai.uid_RSS,
      nama_pegawai: pegawai.nama_pegawai,
      nama_jabatan: pegawai.nama_jabatan,
      pangkat: pegawai.pangkat,
      apel_pagi: pegawai.apel_pagi,
      apel_siang: pegawai.apel_siang,
    })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox mb={3}>
                  <MDBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    gap={2}
                  >
                    <MDBox width={{ xs: "100%", md: "auto" }}>
                      <MDTypography variant="h6" gutterBottom>
                        Statistik Apel Harian
                      </MDTypography>
                      <MDBox
                        display="flex"
                        flexDirection={{ xs: "column", sm: "row" }}
                        flexWrap="wrap"
                        gap={1}
                        mt={1}
                      >
                        <MDButton
                          variant={
                            selectedApelType === "pagi"
                              ? "contained"
                              : "outlined"
                          }
                          color="info"
                          size="small"
                          onClick={() => setSelectedApelType("pagi")}
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          Apel Pagi
                        </MDButton>
                        <MDButton
                          variant={
                            selectedApelType === "siang"
                              ? "contained"
                              : "outlined"
                          }
                          color="info"
                          size="small"
                          onClick={() => setSelectedApelType("siang")}
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          Apel Siang
                        </MDButton>
                        <MDButton
                          variant={
                            selectedApelType === "gabungan"
                              ? "contained"
                              : "outlined"
                          }
                          color="info"
                          size="small"
                          onClick={() => setSelectedApelType("gabungan")}
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          Gabungan
                        </MDButton>
                        <MDButton
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleOpenBulkDialog("Pagi")}
                          disabled={
                            loading || !dataApelDinas?.data_pegawai?.length
                          }
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          Apel Pagi Massal
                        </MDButton>
                        <MDButton
                          variant="contained"
                          color="warning"
                          size="small"
                          onClick={() => handleOpenBulkDialog("Siang")}
                          disabled={
                            loading || !dataApelDinas?.data_pegawai?.length
                          }
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          Apel Siang Massal
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="success"
                          size="small"
                          onClick={fetchApelDinas}
                          disabled={loading}
                          sx={{ flex: { xs: "1 1 auto", sm: "none" } }}
                        >
                          <Icon>refresh</Icon>
                          &nbsp;Refresh
                        </MDButton>
                      </MDBox>
                    </MDBox>
                    <MDBox width={{ xs: "100%", md: "auto" }}>
                      <MDInput
                        type="date"
                        label="Pilih Tanggal"
                        value={selectedTanggal}
                        onChange={handleTanggalChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={isMobile}
                      />
                    </MDBox>
                  </MDBox>
                </MDBox>

                {dataApelDinas && (
                  <>
                    <MDTypography variant="h6" color="text" mb={2}>
                      Rekap Apel Harian - {dataApelDinas.nama_dinas} -{" "}
                      {new Date(dataApelDinas.tanggal).toLocaleDateString(
                        "id-ID"
                      )}
                      (
                      {selectedApelType === "pagi"
                        ? "Apel Pagi"
                        : selectedApelType === "siang"
                        ? "Apel Siang"
                        : "Gabungan Pagi & Siang"}
                      )
                    </MDTypography>

                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Chip
                          icon={<Icon sx={{ color: "white" }}>group</Icon>}
                          label={`Total: ${dataApelDinas.total_pegawai}`}
                          variant="filled"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#424242",
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                            "& .MuiChip-label": {
                              color: "white",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>check_circle</Icon>}
                          label={`Hadir: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Hadir"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Hadir"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Hadir" ||
                                    p.apel_siang?.status === "Hadir"
                                ).length
                          }`}
                          color="success"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>warning</Icon>}
                          label={`Izin: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Izin"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Izin"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Izin" ||
                                    p.apel_siang?.status === "Izin"
                                ).length
                          }`}
                          color="warning"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>local_hospital</Icon>}
                          label={`Sakit: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Sakit"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Sakit"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Sakit" ||
                                    p.apel_siang?.status === "Sakit"
                                ).length
                          }`}
                          color="error"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>work</Icon>}
                          label={`Dinas Luar: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Dinas Luar"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Dinas Luar"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Dinas Luar" ||
                                    p.apel_siang?.status === "Dinas Luar"
                                ).length
                          }`}
                          variant="filled"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#9c27b0",
                            color: "white",
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={
                            <Icon sx={{ color: "white" }}>not_interested</Icon>
                          }
                          label={`Tanpa Berita: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Tanpa Berita"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Tanpa Berita"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Tanpa Berita" ||
                                    p.apel_siang?.status === "Tanpa Berita"
                                ).length
                          }`}
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                            backgroundColor: "#424242",
                            color: "white",
                            "& .MuiChip-icon": {
                              color: "white",
                            },
                            "& .MuiChip-label": {
                              color: "white",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>event_busy</Icon>}
                          label={`Cuti: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Cuti"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_siang?.status === "Cuti"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Cuti" ||
                                    p.apel_siang?.status === "Cuti"
                                ).length
                          }`}
                          color="info"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip
                          icon={<Icon>school</Icon>}
                          label={`Tugas Belajar: ${
                            selectedApelType === "pagi"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) => p.apel_pagi?.status === "Tugas Belajar"
                                ).length
                              : selectedApelType === "siang"
                              ? dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_siang?.status === "Tugas Belajar"
                                ).length
                              : dataApelDinas.data_pegawai.filter(
                                  (p) =>
                                    p.apel_pagi?.status === "Tugas Belajar" ||
                                    p.apel_siang?.status === "Tugas Belajar"
                                ).length
                          }`}
                          color="primary"
                          variant="filled"
                          sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataApelDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Apel Pegawai:{" "}
                        {new Date(selectedTanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataApelDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleExportExcel}
                        disabled={loading}
                      >
                        <Icon>download</Icon>
                        &nbsp;Export Excel
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="error"
                        onClick={handleExportPDF}
                        disabled={loading}
                      >
                        <Icon>picture_as_pdf</Icon>
                        &nbsp;Export PDF
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        rows.map((row, index) => (
                          <Card key={`${row.uid_RSS || "row"}-${index}`}>
                            <CardContent>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <img
                                      src={`https://smartdh.rotendaokab.go.id/uploads/${row.uid_RSS}.jpg`}
                                      alt={row.nama_pegawai}
                                      style={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #e0e0e0",
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                          "flex";
                                      }}
                                    />
                                    <MDBox
                                      display="none"
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        width: 72,
                                        height: 72,
                                        borderRadius: "50%",
                                        backgroundColor: "grey.200",
                                        color: "grey.500",
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 36 }}>person</Icon>
                                    </MDBox>
                                  </MDBox>
                                </Grid>
                                <Grid item xs={8}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                  >
                                    {row.nama_pegawai}
                                  </MDTypography>
                                  <MDBox
                                    mt={1}
                                    display="flex"
                                    flexWrap="wrap"
                                    gap={0.5}
                                  >
                                    <Chip
                                      label={`Pagi: ${
                                        row.apel_pagi && row.apel_pagi.status
                                          ? row.apel_pagi.status
                                          : "Belum Absen"
                                      }`}
                                      color={
                                        row.apel_pagi?.status === "Hadir"
                                          ? "success"
                                          : row.apel_pagi?.status === "Izin"
                                          ? "warning"
                                          : row.apel_pagi?.status === "Sakit"
                                          ? "error"
                                          : row.apel_pagi?.status ===
                                            "Dinas Luar"
                                          ? "secondary"
                                          : "default"
                                      }
                                      size="small"
                                      onClick={() => handleOpenDetailModal(row)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                    <Chip
                                      label={`Siang: ${
                                        row.apel_siang && row.apel_siang.status
                                          ? row.apel_siang.status
                                          : "Belum Absen"
                                      }`}
                                      color={
                                        row.apel_siang?.status === "Hadir"
                                          ? "success"
                                          : row.apel_siang?.status === "Izin"
                                          ? "warning"
                                          : row.apel_siang?.status === "Sakit"
                                          ? "error"
                                          : row.apel_siang?.status ===
                                            "Dinas Luar"
                                          ? "secondary"
                                          : "default"
                                      }
                                      size="small"
                                      onClick={() => handleOpenDetailModal(row)}
                                      sx={{ cursor: "pointer" }}
                                    />
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Jabatan: {row.nama_jabatan || "-"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Pangkat: {row.pangkat || "-"}
                                    </MDTypography>
                                  </MDBox>
                                  <MDBox mt={1}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Jam Pagi:{" "}
                                      {row.apel_pagi && row.apel_pagi.jam_absen
                                        ? row.apel_pagi.jam_absen
                                        : "Belum Absen"}
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                    >
                                      Jam Siang:{" "}
                                      {row.apel_siang &&
                                      row.apel_siang.jam_absen
                                        ? row.apel_siang.jam_absen
                                        : "Belum Absen"}
                                    </MDTypography>
                                  </MDBox>
                                  {userStatus === "admin" ||
                                    (userStatus === "super admin" &&
                                      userId === 9 && (
                                        <MDBox mt={1}>
                                          <MDButton
                                            variant="outlined"
                                            color="info"
                                            size="small"
                                            onClick={() =>
                                              handleOpenDialog(row)
                                            }
                                            sx={{ minWidth: "auto", px: 1 }}
                                          >
                                            <Icon sx={{ fontSize: 16 }}>
                                              edit
                                            </Icon>
                                            &nbsp;Catat
                                          </MDButton>
                                        </MDBox>
                                      ))}
                                </Grid>
                              </Grid>
                            </CardContent>
                          </Card>
                        ))}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                      loading={loading}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data apel dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataApelDinas && !loading && userDinasId && selectedTanggal && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={4}
                  >
                    <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
                      event_busy
                    </Icon>
                    <MDTypography variant="h6" color="text.secondary">
                      Tidak ada data apel harian
                    </MDTypography>
                    <MDTypography variant="body2" color="text.secondary">
                      Coba pilih tanggal lain atau periksa kembali dinas
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Dialog Form Apel */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                {isEditMode ? "Edit" : "Tambah"} Data Apel -{" "}
                {selectedPegawai?.nama_pegawai}
              </MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Tanggal"
                    type="date"
                    value={form.tanggal}
                    onChange={(e) =>
                      setForm({ ...form, tanggal: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nama Pegawai"
                    value={selectedPegawai?.nama_pegawai || ""}
                    variant="outlined"
                    disabled
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={jenisApelOptions}
                    value={form.jenis_apel}
                    onChange={(event, newValue) =>
                      setForm({ ...form, jenis_apel: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Jenis Apel"
                        variant="outlined"
                      />
                    )}
                    disabled={isEditMode}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Jam Absen"
                    type="time"
                    value={form.jam_absen}
                    onChange={(e) =>
                      setForm({ ...form, jam_absen: e.target.value })
                    }
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={statusOptions}
                    value={form.status}
                    onChange={(event, newValue) =>
                      setForm({ ...form, status: newValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={form.keterangan}
                    onChange={(e) =>
                      setForm({ ...form, keterangan: e.target.value })
                    }
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={handleCloseDialog}
              disabled={loadingSubmit}
            >
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleSubmit}
              disabled={loadingSubmit}
              startIcon={
                loadingSubmit ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {loadingSubmit
                ? "Menyimpan..."
                : isEditMode
                ? "Update"
                : "Simpan"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Dialog Catat Apel Massal */}
        <Dialog
          open={openBulkDialog}
          onClose={handleCloseBulkDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                Catat Apel Massal - Apel {bulkType}
              </MDTypography>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={handleSelectAllPegawai}
                  disabled={!pegawaiOptions.length}
                >
                  Pilih Semua
                </MDButton>
              </MDBox>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={pegawaiOptions.filter(
                      (option) =>
                        !selectedPegawaiBulk.some(
                          (selected) => selected.value === option.value
                        )
                    )}
                    value={selectedPegawaiBulk}
                    onChange={(event, newValue) =>
                      setSelectedPegawaiBulk(newValue || [])
                    }
                    getOptionLabel={(option) => option.label || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pilih Pegawai"
                        variant="outlined"
                        placeholder="Cari nama pegawai"
                        helperText={`${selectedPegawaiBulk.length} pegawai sudah dipilih`}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <img
                            src={option.avatar}
                            alt={option.label}
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <MDTypography variant="button">
                            {option.label}
                          </MDTypography>
                        </MDBox>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          label={option.label}
                          avatar={
                            <img
                              src={option.avatar}
                              alt={option.label}
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          }
                        />
                      ))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Jam Absen"
                    type="time"
                    value={bulkJam}
                    onChange={(e) => setBulkJam(e.target.value)}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={statusOptions}
                    value={bulkStatus}
                    onChange={(event, newValue) =>
                      setBulkStatus(newValue || "Hadir")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Keterangan"
                    value={bulkKeterangan}
                    onChange={(e) => setBulkKeterangan(e.target.value)}
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={handleCloseBulkDialog}
              disabled={loadingBulkSubmit}
            >
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleBulkSubmit}
              disabled={loadingBulkSubmit}
              startIcon={
                loadingBulkSubmit ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {loadingBulkSubmit ? "Menyimpan..." : "Simpan Massal"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Modal Detail Apel */}
        <Dialog
          open={openDetailModal}
          onClose={handleCloseDetailModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h5">
                Detail Apel - {selectedApelDetail?.nama_pegawai}
              </MDTypography>
            </MDBox>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2}>
              {selectedApelDetail && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <MDTypography variant="h6" color="info" gutterBottom>
                      Informasi Pegawai
                    </MDTypography>
                    <MDBox p={2} bgcolor="grey.50" borderRadius={1}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Nama: {selectedApelDetail.nama_pegawai}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Jabatan: {selectedApelDetail.nama_jabatan}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Pangkat: {selectedApelDetail.pangkat}
                          </MDTypography>
                        </Grid>
                        <Grid item xs={6}>
                          <MDTypography variant="subtitle2" color="text">
                            Tanggal:{" "}
                            {new Date(selectedTanggal).toLocaleDateString(
                              "id-ID"
                            )}
                          </MDTypography>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" color="success" gutterBottom>
                      Apel Pagi
                    </MDTypography>
                    <Card sx={{ p: 2 }}>
                      <MDBox>
                        <MDTypography
                          variant="subtitle2"
                          color="text"
                          gutterBottom
                        >
                          Status:{" "}
                          {selectedApelDetail.apel_pagi?.status ||
                            "Belum Absen"}
                        </MDTypography>
                        {selectedApelDetail.apel_pagi?.jam_absen && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Jam: {selectedApelDetail.apel_pagi.jam_absen}
                          </MDTypography>
                        )}
                        {selectedApelDetail.apel_pagi?.keterangan && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Keterangan:{" "}
                            {selectedApelDetail.apel_pagi.keterangan}
                          </MDTypography>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <MDTypography variant="h6" color="primary" gutterBottom>
                      Apel Siang
                    </MDTypography>
                    <Card sx={{ p: 2 }}>
                      <MDBox>
                        <MDTypography
                          variant="subtitle2"
                          color="text"
                          gutterBottom
                        >
                          Status:{" "}
                          {selectedApelDetail.apel_siang?.status ||
                            "Belum Absen"}
                        </MDTypography>
                        {selectedApelDetail.apel_siang?.jam_absen && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Jam: {selectedApelDetail.apel_siang.jam_absen}
                          </MDTypography>
                        )}
                        {selectedApelDetail.apel_siang?.keterangan && (
                          <MDTypography
                            variant="subtitle2"
                            color="text"
                            gutterBottom
                          >
                            Keterangan:{" "}
                            {selectedApelDetail.apel_siang.keterangan}
                          </MDTypography>
                        )}
                      </MDBox>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton color="secondary" onClick={handleCloseDetailModal}>
              Tutup
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ApelDinas;
